import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, MapPin, Phone, Mail, Globe, Clock, Star, Tag, CalendarPlus } from 'lucide-react';
import { patientApi } from '../api';
import type { MarketplaceProvider, ProviderCertificate, ProviderPricing } from '../types';
import VerifiedBadge from '../components/VerifiedBadge';
import CertificatePanel from '../components/CertificatePanel';
import PriceComparisonTable from '../components/PriceComparisonTable';
import ProviderReviews from '../components/ProviderReviews';
import FavoriteButton from '../components/FavoriteButton';
import BookDoctorForm from '../components/BookDoctorForm';
import BookLabForm from '../components/BookLabForm';
import BookAmbulanceForm from '../components/BookAmbulanceForm';
import BookHospitalForm from '../components/BookHospitalForm';
import Spinner from '../../../components/ui/Spinner';

const TYPE_ICONS: Record<string, string> = {
  DOCTOR: '🩺', LAB: '🔬', HOSPITAL: '🏥', PHARMACY: '💊', AMBULANCE: '🚑',
};

function parseJson<T>(val: T | string | undefined | null): T | [] {
  if (!val) return [] as any;
  if (typeof val === 'string') {
    try { return JSON.parse(val); } catch { return [] as any; }
  }
  return val;
}

export default function ProviderDetail() {
  const { slug, type } = useParams<{ slug: string; type: string }>();
  const navigate = useNavigate();
  const [provider, setProvider] = useState<MarketplaceProvider | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let alive = true;
    setLoading(true);

    Promise.all([
      patientApi.getMarketplaceProvider(slug),
      patientApi.getFavoriteIds().catch(() => [] as string[]),
    ])
      .then(([p, favIds]) => {
        if (alive) {
          setProvider(p);
          setIsFavorited(favIds.includes(p.id));
        }
      })
      .catch(() => { if (alive) setNotFound(true); })
      .finally(() => { if (alive) setLoading(false); });

    return () => { alive = false; };
  }, [slug]);

  if (loading) return <div className="flex justify-center py-12"><Spinner /></div>;

  if (notFound || !provider) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 mb-3">Provider not found</p>
        <button onClick={() => navigate(-1)} className="text-sm text-teal-600 hover:text-teal-700">Go back</button>
      </div>
    );
  }

  const meta = provider.metadata as Record<string, any> | null;
  const certificates = parseJson<ProviderCertificate[]>(provider.certificates);
  const pricing = parseJson<ProviderPricing[]>(provider.pricing);

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Back */}
      <button
        onClick={() => navigate(`/patient/marketplace/${type || provider.type.toLowerCase()}`)}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        <ChevronLeft className="w-4 h-4" /> Back to listing
      </button>

      {/* Header Card */}
      <div className="card p-6">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center text-3xl shrink-0">
            {provider.logoUrl ? (
              <img src={provider.logoUrl} alt={provider.name} className="w-12 h-12 rounded-xl object-cover" />
            ) : (
              TYPE_ICONS[provider.type]
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-gray-900">{provider.name}</h1>
              {provider.isVerified && <VerifiedBadge size="md" />}
            </div>
            <p className="text-sm text-gray-500 mt-0.5 capitalize">{provider.type.toLowerCase()}</p>

            {provider.rating > 0 && (
              <div className="flex items-center gap-1 mt-2">
                <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                <span className="text-sm font-semibold text-gray-900">{provider.rating.toFixed(1)}</span>
                <span className="text-sm text-gray-400">({provider.reviewCount} review{provider.reviewCount !== 1 ? 's' : ''})</span>
              </div>
            )}
          </div>

          <FavoriteButton providerId={provider.id} initialFavorited={isFavorited} />
        </div>

        {provider.description && (
          <p className="text-sm text-gray-600 mt-4 leading-relaxed">{provider.description}</p>
        )}

        {provider.specialties.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-4">
            {provider.specialties.map(s => (
              <span key={s} className="text-xs font-medium px-2 py-1 rounded-lg bg-teal-50 text-teal-700">{s}</span>
            ))}
          </div>
        )}
      </div>

      {/* Certificates */}
      <CertificatePanel certificates={certificates} />

      {/* Pricing */}
      <PriceComparisonTable pricing={pricing} />

      {/* Contact & Location */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="card p-4 space-y-3">
          <h3 className="text-sm font-bold text-gray-900">Contact</h3>
          {provider.phone && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Phone className="w-4 h-4 text-gray-400" /> {provider.phone}
            </div>
          )}
          {provider.email && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Mail className="w-4 h-4 text-gray-400" /> {provider.email}
            </div>
          )}
          {provider.website && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Globe className="w-4 h-4 text-gray-400" /> {provider.website}
            </div>
          )}
          {provider.openHours && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Clock className="w-4 h-4 text-gray-400" /> {provider.openHours}
            </div>
          )}
        </div>

        <div className="card p-4 space-y-3">
          <h3 className="text-sm font-bold text-gray-900">Location</h3>
          {provider.address && (
            <div className="flex items-start gap-2 text-sm text-gray-600">
              <MapPin className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
              <span>{provider.address}{provider.city ? `, ${provider.city}` : ''}{provider.state ? `, ${provider.state}` : ''}{provider.pincode ? ` - ${provider.pincode}` : ''}</span>
            </div>
          )}
          {provider.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {provider.tags.map(t => (
                <span key={t} className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                  <Tag className="w-3 h-3" /> {t}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Details — whitelisted, human-facing metadata only. Dumping the raw
          metadata object rendered internal keys to patients as facts. */}
      {meta && (() => {
        const DISPLAY_KEYS: Record<string, string> = {
          experienceYears: 'Experience (years)', experience: 'Experience',
          qualification: 'Qualification', qualifications: 'Qualifications',
          languages: 'Languages', hospitalAffiliation: 'Hospital Affiliation',
          registrationNumber: 'Registration No.', specialization: 'Specialization',
          beds: 'Beds', departments: 'Departments', accreditation: 'Accreditation',
        };
        const rows = Object.entries(meta).filter(([k, v]) => DISPLAY_KEYS[k] && v != null && String(v).trim() !== '');
        return rows.length > 0 ? (
          <div className="card p-4">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Details</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {rows.map(([key, value]) => (
                <div key={key}>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider">{DISPLAY_KEYS[key]}</p>
                  <p className="text-sm font-medium text-gray-900 mt-0.5">{Array.isArray(value) ? value.join(', ') : String(value)}</p>
                </div>
              ))}
            </div>
          </div>
        ) : null;
      })()}

      {/* What patients actually wrote — the star average at the top of this
          page had no visible basis until now. */}
      <div>
        <p className="text-sm font-semibold text-gray-900 mb-2">Patient reviews</p>
        <ProviderReviews providerKey={provider.slug || provider.id} />
      </div>

      {/* Patient Safety Note */}
      <div className="rounded-xl bg-blue-50/60 border border-blue-100 px-4 py-3">
        <p className="text-[10px] text-gray-500 leading-relaxed">Booking confirmation depends on provider availability. Your medical records are shared only with your consent.</p>
      </div>

      {/* Book Now — pharmacies route to the REAL online storefront (orders
          land in the pharmacy's own orderbook); the old marketplace medicine
          order form posted into a queue no pharmacy panel ever read. */}
      <div className="card p-4">
        <button
          onClick={() => {
            if (provider.type === 'PHARMACY') {
              navigate(`/patient/care-services/pharmacy/${provider.slug}`);
            } else {
              setBookingOpen(true);
            }
          }}
          className="btn-primary w-full flex items-center justify-center gap-2"
        >
          <CalendarPlus className="w-5 h-5" />
          {BOOKING_LABELS[provider.type] || 'Book Now'}
        </button>
      </div>

      {/* Booking Modals */}
      {provider.type === 'DOCTOR' && (
        <BookDoctorForm provider={provider} open={bookingOpen} onClose={() => setBookingOpen(false)} />
      )}
      {provider.type === 'LAB' && (
        <BookLabForm provider={provider} pricing={pricing} open={bookingOpen} onClose={() => setBookingOpen(false)} />
      )}
      {provider.type === 'HOSPITAL' && (
        <BookHospitalForm provider={provider} open={bookingOpen} onClose={() => setBookingOpen(false)} />
      )}
      {provider.type === 'AMBULANCE' && (
        <BookAmbulanceForm provider={provider} open={bookingOpen} onClose={() => setBookingOpen(false)} />
      )}
    </div>
  );
}

const BOOKING_LABELS: Record<string, string> = {
  DOCTOR: 'Book Appointment',
  LAB: 'Book Lab Test',
  HOSPITAL: 'Book OPD Appointment',
  PHARMACY: 'Order Medicines',
  AMBULANCE: 'Request Ambulance',
};
